"use client"

import { useCallback, useEffect, useState } from "react"
import { StoreCart } from "@medusajs/types"
import CartDropdown from "../cart-dropdown"
import { CART_UPDATED_EVENT } from "@lib/util/cart-events"

type CartSessionPayload = {
  cart: StoreCart | null
}

export default function CartButton() {
  const [cart, setCart] = useState<StoreCart | null>(null)

  const loadCart = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch("/api/cart-session", {
        cache: "no-store",
        signal,
      })

      if (!response.ok) {
        return
      }

      const payload = (await response.json()) as CartSessionPayload
      setCart(payload.cart)
    } catch (err: any) {
      // Ignore aborts and other cart widget failures to avoid blocking the page.
      if (err?.name !== "AbortError") {
        console.warn("[CartButton] Failed to load cart:", err)
      }
    }
  }, [])

  // Initial load
  useEffect(() => {
    const controller = new AbortController()
    loadCart(controller.signal)
    return () => controller.abort()
  }, [loadCart])

  // Listen for cart updates and re-fetch (with a small delay to let the
  // backend cart-session cookie propagate, e.g. after a login redirect)
  useEffect(() => {
    let delayTimer: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null

    const handleUpdate = () => {
      // Immediate fetch
      controller?.abort()
      controller = new AbortController()
      loadCart(controller.signal)

      // Delayed fetch to catch cookie-propagation lag after login
      if (delayTimer) clearTimeout(delayTimer)
      delayTimer = setTimeout(() => {
        controller?.abort()
        controller = new AbortController()
        loadCart(controller.signal)
      }, 700)
    }

    window.addEventListener(CART_UPDATED_EVENT, handleUpdate)

    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, handleUpdate)
      controller?.abort()
      if (delayTimer) clearTimeout(delayTimer)
    }
  }, [loadCart])

  return <CartDropdown cart={cart} />
}
